import { gql } from '@apollo/client';
import React from 'react';

import AffirmEstimateCTA from '@reverbdotcom/commons/src/components/affirm_estimate_cta';
import { useExpEnabled, useUser } from '@reverbdotcom/commons/src/user_hooks';
import experiments from '@reverbdotcom/commons/src/experiments';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';

import { ListingAffirmCalloutFields } from '../schemas/rql';
import AffirmZeroPercentNudge from './affirm_estimate_cta/affirm_zero_percent_nudge';
import { useAffirm } from './useAffirm';

type Listing = ListingAffirmCalloutFields.Fragment;

interface Props {
  listing: Listing;
  simpleZeroPercentNudge?: boolean;
}

interface IAffirmExperimentCallout {
  amountCents: number;
  financingProgramSlug: string;
}

const AFFIRM_PAGE_TYPE = 'product';

function AffirmExperimentCallout({
  amountCents,
  financingProgramSlug,
}: IAffirmExperimentCallout) {

  const promoId = `promo_set_${financingProgramSlug}`;
  useAffirm();

  return (
    <div
      className="affirm-as-low-as"
      data-page-type={AFFIRM_PAGE_TYPE}
      data-amount={amountCents}
      data-promo-id={promoId}
    />
  );
}

export function ListingAffirmCallout({
  listing,
  simpleZeroPercentNudge = false,
}: Props) {

  const { estimatedMonthlyPaymentPlan, pricing, shippingPrice } = listing;
  const { buyerPrice } = pricing || {};
  const {
    availableTermMonths,
    estimatedMonthlyPayment,
    maxTermMonth,
    lowestAprAtMaxTerm,
    zeroPercentFinancingPlan,
    splitPay,
    financingProgramSlug,
  } = estimatedMonthlyPaymentPlan || {};

  const buyerPriceDisplay = buyerPrice?.display;
  const productAmountCents = buyerPrice?.amountCents || 0;
  const shippingAmountCents = shippingPrice?.amountCents || 0;
  const totalFinancingAmountCents = productAmountCents + shippingAmountCents;
  const emptyRender = !estimatedMonthlyPayment?.display || !buyerPriceDisplay;

  const expEnabled = useExpEnabled(experiments.AFFIRM_CALLOUT_PREQUALIFICATION);
  const user = useUser();

  useViewTracking({
    componentName: 'ListingAffirmCallout',
    eventName: MParticleEventName.ComponentView,
    experiments: formatExperimentsForEventAttributes(user, [
      experiments.AFFIRM_CALLOUT_PREQUALIFICATION,
    ]),
    financingProgramSlug,
  }, !emptyRender);

  if (emptyRender) { return null; }

  if (expEnabled) {
    return (
      <AffirmExperimentCallout
        amountCents={totalFinancingAmountCents}
        financingProgramSlug={financingProgramSlug}
      />
    );
  }

  if (zeroPercentFinancingPlan && !simpleZeroPercentNudge) {
    return (
      <AffirmZeroPercentNudge
        estimatedMonthlyPayment={estimatedMonthlyPayment.display}
        buyerPrice={buyerPriceDisplay}
        maxTermMonth={maxTermMonth}
        availableTermMonths={availableTermMonths}
        lowestAprAtMaxTerm={lowestAprAtMaxTerm}
      />
    );
  }

  return (
    <AffirmEstimateCTA
      estimatedMonthlyPayment={estimatedMonthlyPayment.display}
      buyerPrice={buyerPriceDisplay}
      maxTermMonth={maxTermMonth}
      availableTermMonths={availableTermMonths}
      splitPay={splitPay}
      lowestAprAtMaxTerm={lowestAprAtMaxTerm}
      zeroPercentFinancing={zeroPercentFinancingPlan}
    />
  );
}

export const ListingAffirmCalloutFragment = gql`
  fragment ListingAffirmCalloutFields on Listing {
    _id
    estimatedMonthlyPaymentPlan {
      estimatedMonthlyPayment {
        display
      }
      maxTermMonth
      availableTermMonths
      lowestAprAtMaxTerm
      zeroPercentFinancingPlan
      splitPay
      financingProgramSlug
    },
    pricing {
      buyerPrice {
        display
        amountCents
      }
    },
    shippingPrice {
      amountCents
    }
  }
`;
